import React, { useState, useRef, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { useTheme, ThemedButton, getSlotColors } from './ThemeContext';

const formatMove = (move, isDark) => {
  if (!move || move.san === '❓') return '❓';

  let san = move.san;
  const isCheck = san.endsWith('+');
  const isCheckmate = san.endsWith('#');

  if (san.includes('=')) {
    let cleanSan = san;
    if (isCheck) cleanSan = san.slice(0, -1);
    if (isCheckmate) cleanSan = san.slice(0, -1);

    const promotedPiece = cleanSan.split('=')[1].toLowerCase();
    const pieceSymbol = pieceToUnicode(promotedPiece, move.color, isDark);

    return san.replace(/=[QRBN]/i, `=${pieceSymbol}`);
  }

  if (move?.piece === 'p' || san.includes('-')) {
    return pieceToUnicode(move.piece, move.color, isDark) + san;
  }
  return pieceToUnicode(move.piece, move.color, isDark) + san.slice(1);
};

const pieceToUnicode = (piece, color, isDark) => {
  const symbols = {
    'k': color === 'w' ? (!isDark ? '♔' : '♚') : (isDark ? '♔' : '♚'),
    'q': color === 'w' ? (!isDark ? '♕' : '♛') : (isDark ? '♕' : '♛'),
    'r': color === 'w' ? (!isDark ? '♖' : '♜') : (isDark ? '♖' : '♜'),
    'b': color === 'w' ? (!isDark ? '♗' : '♝') : (isDark ? '♗' : '♝'),
    'n': color === 'w' ? (!isDark ? '♘' : '♞') : (isDark ? '♘' : '♞'),
    'p': color === 'w' ? (!isDark ? '♙' : '♟') : (isDark ? '♙' : '♟')
  };
  return symbols[piece?.toLowerCase()] || '';
};

const GoldenSparkle = () => (
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute inset-0 border-4 border-yellow-400 rounded-lg animate-pulse">
      <div className="absolute -top-1 -left-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping"></div>
      <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping delay-100"></div>
      <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping delay-200"></div>
      <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping delay-300"></div>
    </div>
  </div>
);

const SlotMachine = ({
  slots = Array(3).fill(null),
  onSlotClick,
  selectedSlot,
  isSpinning,
  finMove,
  showGoldenBorder,
  onReset,
  onRandomMove,
  onSubmit,
  anarchyMode = false,
  disabled = false,
  canSubmit = false,
  isOpponent = false,
  isSpectator,
  currentColor,
  color
}) => {
  const { isDark, woodStyles } = useTheme();
  const slotColors = getSlotColors(woodStyles);

  const N = 10; // Number of spin cycles before settling on final move

  // Track displayed moves during spinning animation
  const [displayedMoves, setDisplayedMoves] = useState([null, null, null]);

  // Set up animation cycle counters for each slot
  const animationCycleRef = useRef([0, 0, 0]);
  const cycleTimeoutsRef = useRef([null, null, null]);

  const getDisplayMove = (slot) => {
    if (slot === null) {
      if (currentColor !== color) {
        const nonActiveColorFinMove = finMove?.[currentColor === 'white' ? 'black' : 'white'];
        return nonActiveColorFinMove || { san: '❓' };
      }
      return { san: '❓' };
    }
    return slot;
  };

  // Function to generate a pool of possible moves for animation
  const getMovePool = (slotIndex) => {
    const availableMoves = slots.filter(Boolean);
    const finalMove = currentColor !== color
      ? finMove?.[currentColor === 'white' ? 'black' : 'white']
      : finMove?.[color];

    // Start with all available moves
    let movePool = [...availableMoves, ...availableMoves, ...availableMoves];



    // Ensure we have at least one move
    if (movePool.length === 0) {
      movePool = [{ san: '❓' }];
    }

    return movePool;
  };

  // Update displayed moves on each animation cycle
  const updateDisplayedMove = (slotIndex) => {
    if (!isSpinning) return;

    const movePool = getMovePool(slotIndex);
    const cycleCount = animationCycleRef.current[slotIndex];

    // Choose a random move from the pool for animation cycles
    const randomIndex = Math.floor(Math.random() * movePool.length);
    const moveToShow = cycleCount >= N
      ? (finMove?.[color] || slots[slotIndex] || { san: '❓' })  // Final move
      : movePool[randomIndex]; // Random move for animation

    setDisplayedMoves(prev => {
      const newMoves = [...prev];
      newMoves[slotIndex] = moveToShow;
      return newMoves;
    });

    // Increment cycle counter
    animationCycleRef.current[slotIndex]++;

    // Continue cycling if not at the end
    if (cycleCount < N + 3) { // Add a few extra cycles to ensure we show the final move
      const timing = getSlotTiming(slotIndex);
      const duration = parseInt(timing.duration) * 2; // Time for one full cycle (up + down)

      cycleTimeoutsRef.current[slotIndex] = setTimeout(() => {
        updateDisplayedMove(slotIndex);
      }, duration);
    }
  };

  // Start the animation when spinning begins
  useEffect(() => {
    if (isSpinning) {
      // Reset animation cycle counters
      animationCycleRef.current = [0, 0, 0];

      // Clear any existing timeouts
      cycleTimeoutsRef.current.forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });

      // Start animations with slight delays between slots
      [0, 1, 2].forEach(index => {
        setTimeout(() => {
          updateDisplayedMove(index);
        }, index * 10); // Staggered start for each slot
      });
    } else {
      // Clear timeouts when spinning stops
      cycleTimeoutsRef.current.forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });

      // Reset displayed moves when not spinning
      setDisplayedMoves([null, null, null]);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      cycleTimeoutsRef.current.forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [isSpinning]);

  const getRandomMove = (slot, index) => {
    if (!isSpinning) {
      return formatMove(getDisplayMove(slot), isDark);
    }

    // During spinning, use the currently displayed move for this slot
    return formatMove(displayedMoves[index] || getDisplayMove(slot), isDark);
  };

  const getSlotTiming = (index) => ({
    duration: `${(1 + index * 0.25) * 80}ms`, // Duration varies by slot
    timing: 'cubic-bezier(.41,.23,.3,1.07)', // Smooth easing
    delay: `${(1 + index - 2 * Math.random()) * 10}ms` // Staggered delay
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-12 gap-1">
        {slots.map((slot, index) => {
          const hasValue = !isOpponent && slot;
          const isSlotSelected = selectedSlot === index;
          const timing = getSlotTiming(index);
          const currentMove = getRandomMove(slot, index);

          return (
            <div
              key={index}
              onClick={() => !disabled && !isOpponent && onSlotClick?.(index)}
              className={`
                relative overflow-hidden rounded-lg flex-1
                border-2 transition-all duration-500
                ${!disabled ? woodStyles.surface.primary : woodStyles.surface.secondary}
                ${(anarchyMode && index === 2) || disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}
              `}
              style={{
                borderColor: hasValue
                  ? slotColors[index].active
                  : isSlotSelected
                    ? slotColors[index].base
                    : slotColors[index].base,
                boxShadow: hasValue
                  ? slotColors[index].glow
                  : isSlotSelected
                    ? `0 0 5px ${slotColors[index].base}`
                    : 'none'
              }}
            >
              {!isOpponent && showGoldenBorder && <GoldenSparkle />}
              {anarchyMode && index === 2 && (
                <div className="absolute top-1 right-1 z-10">
                  <Lock className="w-3 h-3 text-red-500" />
                </div>
              )}

              <div className="boxes h-full flex items-center justify-center text-xl font-mono relative overflow-hidden">
                {isSpinning ? (
                  <div className={`absolute inset-0 flex flex-col items-center justify-center ${slotColors[index].text}`}>
                    {/* First move (current) - adjusted animation */}
                    <div
                      className={`absolute w-full h-full flex items-center justify-center
                        ${woodStyles.surface.primary} bg-opacity-50`}
                      style={{
                        transform: 'translateY(0%)',
                        animation: `slot-spin ${timing.duration} ${timing.timing} ${timing.delay} infinite`
                      }}
                    >
                      {currentMove}
                    </div>
                    {/* Second move - adjusted animation */}
                    <div
                      className={`absolute w-full h-full flex items-center justify-center
                        ${woodStyles.surface.primary} bg-opacity-30`}
                      style={{
                        transform: 'translateY(0%)',
                        animation: `slot-spin ${timing.duration} ${timing.timing} infinite`
                      }}
                    >
                      {currentMove}
                    </div>
                  </div>
                ) : (
                  <span
                    className={`${slotColors[index].text} transition-all duration-200 ease-in inline-block`}
                    style={{
                      animation: 'slot-bounce 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      animationDelay: `${(index - 1) * 0.1}s`
                    }}
                  >
                    {formatMove(getDisplayMove(slot), isDark)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isOpponent && !isSpectator && (
        <div className="flex gap-2">
          <ThemedButton
            onClick={onReset}
            disabled={disabled || isSpinning}
            variant="secondary"
            className="flex-1"
          >
            Reset
          </ThemedButton>
          <ThemedButton
            onClick={canSubmit ? onSubmit : onRandomMove}
            disabled={disabled || isSpinning}
            variant={canSubmit ? "success" : "primary"}
            className="flex-1"
          >
            {canSubmit ? 'Submit' : 'Random'}
          </ThemedButton>
        </div>
      )}
    </div>
  );
};

export default SlotMachine;